import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import { useProject } from "../app/projectContext";

import {
  createBusbarEvidenceRun,
  type BusbarEvidenceRunCreateRequest,
  type BusbarEvidenceRunResponse,
} from "../services/busbarEvidence";

/**
 * Busbar evidence study mutation (EOS-07a).
 *
 * Mirrors useBreakerSelectivity:
 * - One in-flight request at a time: a new submit aborts the previous one.
 * - The in-flight request is aborted on unmount so no state update lands on
 *   an unmounted component.
 * - The run is attached to the revision chosen in the top bar, read at submit time; with
 *   no project, or one without an open revision, nothing is sent and the run is unassigned.
 * - Every calculation is persisted as a run (Master Prompt A16, A24); the typed
 *   result and the run summary are exposed exactly as parsed, including a
 *   NO_SOLUTION result - a busbar whose declared rating does not meet the design
 *   current or the fault level is engineering evidence too.
 * - A linked fault run must belong to the revision the study is saved in; the backend
 *   says so (409) and the message reaches the page unchanged.
 *
 * The request is the whole run body, so the study's notes travel with it; the
 * form never puts a project revision in it.
 */
export function useBusbarEvidence() {
  const controllerRef = useRef<AbortController | null>(null);
  const { activeRevisionId } = useProject();

  const mutation = useMutation<
    BusbarEvidenceRunResponse,
    Error,
    BusbarEvidenceRunCreateRequest
  >({
    mutationKey: ["electrical", "busbar-evidence", "runs", "create"],
    mutationFn: (payload) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      return createBusbarEvidenceRun(
        payload,
        controller.signal,
        activeRevisionId() ?? undefined,
      );
    },
  });

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const calculate = useCallback(
    (payload: BusbarEvidenceRunCreateRequest) => mutation.mutateAsync(payload),
    [mutation],
  );

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    mutation.reset();
  }, [mutation]);

  return {
    calculate,
    reset,
    result: mutation.data?.result ?? null,
    run: mutation.data?.run ?? null,
    error: mutation.error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
}

export type UseBusbarEvidenceResult = ReturnType<typeof useBusbarEvidence>;
